// 암호화 금고(IndexedDB). 모든 레코드 값은 AES-GCM으로 암호화해 저장한다.
// 개발자 도구로 IndexedDB를 열어도 평문이 보이지 않는다(goals.md 성공 기준).
import {
  type Cipher, PBKDF2_ITERATIONS, decryptJSON, deriveKey, encryptJSON, randomBytes, toB64,
} from './crypto'

const DB_NAME = 'mp-vault'
const DB_VERSION = 1
const META = 'meta'
const RECORDS = 'records'
const CRYPTO_KEY = 'crypto'

export class WrongPasswordError extends Error {
  constructor() { super('wrong password'); this.name = 'WrongPasswordError' }
}

interface CryptoMeta { k: string; salt: string; iterations: number; verifier: Cipher }
interface RecordRow { id: string; table: string; data: Cipher }

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'k' })
      if (!db.objectStoreNames.contains(RECORDS)) {
        const s = db.createObjectStore(RECORDS, { keyPath: 'id' })
        s.createIndex('table', 'table', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function tx<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode)
    const req = fn(t.objectStore(store))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function readMeta(db: IDBDatabase): Promise<CryptoMeta | undefined> {
  return tx<CryptoMeta>(db, META, 'readonly', s => s.get(CRYPTO_KEY) as IDBRequest<CryptoMeta>)
}

export async function isInitialized(): Promise<boolean> {
  const db = await openDB()
  try { return !!(await readMeta(db)) } finally { db.close() }
}

/** 첫 실행: 비밀번호로 금고를 만든다. 성공하면 메모리용 키를 돌려준다. */
export async function setup(password: string): Promise<CryptoKey> {
  const db = await openDB()
  try {
    const salt = randomBytes(16)
    const key = await deriveKey(password, salt, PBKDF2_ITERATIONS)
    const verifier = await encryptJSON(key, { v: 'ok' }) // 비번 검증용 토큰
    const meta: CryptoMeta = { k: CRYPTO_KEY, salt: toB64(salt), iterations: PBKDF2_ITERATIONS, verifier }
    await tx(db, META, 'readwrite', s => s.put(meta))
    return key
  } finally { db.close() }
}

/** 잠금 해제: 맞으면 키, 틀리면 WrongPasswordError. */
export async function unlock(password: string): Promise<CryptoKey> {
  const db = await openDB()
  try {
    const meta = await readMeta(db)
    if (!meta) throw new Error('vault not initialized')
    const salt = Uint8Array.from(atob(meta.salt), c => c.charCodeAt(0))
    const key = await deriveKey(password, salt, meta.iterations)
    try {
      const check = await decryptJSON<{ v: string }>(key, meta.verifier)
      if (check.v !== 'ok') throw new WrongPasswordError()
    } catch {
      throw new WrongPasswordError() // AES-GCM 인증 실패 = 비번 틀림
    }
    return key
  } finally { db.close() }
}

/** 비밀번호가 금고 비번과 맞는지만 확인한다(키/상태는 바꾸지 않음). 위장 해제에 쓴다. */
export async function verify(password: string): Promise<boolean> {
  const db = await openDB()
  try {
    const meta = await readMeta(db)
    if (!meta) return false
    const salt = Uint8Array.from(atob(meta.salt), c => c.charCodeAt(0))
    const key = await deriveKey(password, salt, meta.iterations)
    try {
      const check = await decryptJSON<{ v: string }>(key, meta.verifier)
      return check.v === 'ok'
    } catch { return false }
  } finally { db.close() }
}

export async function putRecord(key: CryptoKey, table: string, id: string, value: unknown): Promise<void> {
  const db = await openDB()
  try {
    const row: RecordRow = { id: `${table}:${id}`, table, data: await encryptJSON(key, value) }
    await tx(db, RECORDS, 'readwrite', s => s.put(row))
  } finally { db.close() }
}

export async function getRecord<T = unknown>(key: CryptoKey, table: string, id: string): Promise<T | null> {
  const db = await openDB()
  try {
    const row = await tx<RecordRow | undefined>(db, RECORDS, 'readonly', s => s.get(`${table}:${id}`) as IDBRequest<RecordRow | undefined>)
    return row ? await decryptJSON<T>(key, row.data) : null
  } finally { db.close() }
}

export async function getRecords<T = unknown>(key: CryptoKey, table: string): Promise<T[]> {
  const db = await openDB()
  try {
    const rows = await tx<RecordRow[]>(db, RECORDS, 'readonly', s => s.index('table').getAll(table) as IDBRequest<RecordRow[]>)
    return Promise.all(rows.map(r => decryptJSON<T>(key, r.data)))
  } finally { db.close() }
}

export async function deleteRecord(table: string, id: string): Promise<void> {
  const db = await openDB()
  try { await tx(db, RECORDS, 'readwrite', s => s.delete(`${table}:${id}`)) } finally { db.close() }
}

/** 비밀번호 변경: 새 키로 검증토큰과 모든 레코드를 다시 암호화한다. 새 키를 돌려준다. */
export async function changePassword(oldKey: CryptoKey, newPassword: string): Promise<CryptoKey> {
  const db = await openDB()
  try {
    const salt = randomBytes(16)
    const newKey = await deriveKey(newPassword, salt, PBKDF2_ITERATIONS)
    const verifier = await encryptJSON(newKey, { v: 'ok' })
    const rows = await tx<RecordRow[]>(db, RECORDS, 'readonly', s => s.getAll() as IDBRequest<RecordRow[]>)
    const reenc: RecordRow[] = []
    for (const row of rows) reenc.push({ id: row.id, table: row.table, data: await encryptJSON(newKey, await decryptJSON(oldKey, row.data)) })
    const meta: CryptoMeta = { k: CRYPTO_KEY, salt: toB64(salt), iterations: PBKDF2_ITERATIONS, verifier }
    await tx(db, META, 'readwrite', s => s.put(meta))
    for (const r of reenc) await tx(db, RECORDS, 'readwrite', s => s.put(r))
    return newKey
  } finally { db.close() }
}

/** 데이터 전체 삭제(설정의 '전체 삭제', 또는 비번 분실 시 새로 시작). 되돌릴 수 없다. */
export async function wipe(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
    req.onblocked = () => resolve()
  })
}
